import * as React from 'react';
import Slider from 'react-slick';
import type { Settings as SliderSettings } from 'react-slick';

import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

import BrowseDegreeCard from 'bundles/browse/components/ProductCard/BrowseDegreeCard';
import type { DegreeProductMember } from 'bundles/browse/types/degree-list';
import ArrowButton from 'bundles/enterprise-legacy-xdp/components/products-slider/ArrowButton';
import TrackedButton from 'bundles/page/components/TrackedButton';

import 'css!bundles/enterprise-legacy-xdp/components/products-slider/__styles__/DegreeProductsSlider';
import 'css!bundles/enterprise-legacy-xdp/components/products-slider/__styles__/ProductsSlider';

export type Props = {
  products: Array<DegreeProductMember>;
  fullWidth?: boolean;
  footerLabel?: string;
  forceOpenInNewTab?: boolean;
};

const styles = StyleSheet.create({
  fullWidth: { width: '100% !important' },
  sliderWidth: {
    width: 1419,
    margin: '0 auto',
    [`@media (min-width: ${breakPoint.xs}px)`]: {
      width: 300,
    },
    [`@media (min-width: ${breakPoint.sm}px)`]: {
      width: 456,
    },
    [`@media (min-width: ${breakPoint.md}px)`]: {
      width: 684,
    },
    [`@media (min-width: ${breakPoint.lg}px)`]: {
      width: 912,
    },
    [`@media (min-width: ${breakPoint.xxl}px)`]: {
      width: 1368,
    },
  },
});

export default class ProductsSlider extends React.Component<Props> {
  declare timeoutId: number | undefined;

  static defaultProps = {
    products: [],
  };

  componentDidMount() {
    this.timeoutId = window.setTimeout(() => {
      let event;
      if (typeof Event === 'function') {
        event = new Event('resize');
      } else {
        // IE 11 compatibility: https://stackoverflow.com/questions/27176983/dispatchevent-not-working-in-ie11
        event = window.document.createEvent('Event');
        event.initEvent('resize', true, true);
      }
      window.dispatchEvent(event);
    }, 0);
  }

  getSlidesToShowLength = () => {
    const pageWidth = window.innerWidth;
    let slidesToShowLength = 4;

    if (pageWidth <= breakPoint.sm) {
      slidesToShowLength = 1;
    } else if (pageWidth <= breakPoint.md) {
      slidesToShowLength = this.slidesToShowMd;
    } else if (pageWidth <= breakPoint.lg) {
      slidesToShowLength = this.slidesToShowL;
    } else if (pageWidth <= breakPoint.xl) {
      slidesToShowLength = this.slidesToShowXl;
    }

    return slidesToShowLength;
  };

  slidesToShowXl = 4;

  slidesToShowL = 3;

  slidesToShowMd = 2;

  // If number of elements are below the set slidesToShow, the slider breaks.
  // This sets the slider to only show the available number of slides.
  // Modified from https://github.com/kenwheeler/slick/issues/1504#issuecomment-118461292
  setSliderSettings = (numProducts: number): SliderSettings => {
    const { products } = this.props;
    const slidesToShowLength = this.getSlidesToShowLength();

    let slidesToShowNumbers = [this.slidesToShowXl, this.slidesToShowL, this.slidesToShowMd];

    slidesToShowNumbers = slidesToShowNumbers.map((initialSlides) => {
      if (initialSlides > numProducts) {
        return numProducts;
      } else {
        return initialSlides;
      }
    });

    const slidesToShow = {
      slidesToShowXl: slidesToShowNumbers[0],
      slidesToShowL: slidesToShowNumbers[1],
      slidesToShowMd: slidesToShowNumbers[2],
    };

    return {
      infinite: false,
      nextArrow: (
        <ArrowButton
          isArrowRight
          slider={this.slickRef}
          length={products.length}
          slidesToShowLength={slidesToShowLength}
        />
      ),
      prevArrow: (
        <ArrowButton slider={this.slickRef} length={products.length} slidesToShowLength={slidesToShowLength} />
      ),
      speed: 500,
      slidesToShow: slidesToShow.slidesToShowXl,
      slidesToScroll: slidesToShow.slidesToShowXl,
      dots: false,
      initialSlide: 0,
      responsive: [
        {
          breakpoint: breakPoint.xl,
          settings: {
            slidesToShow: slidesToShow.slidesToShowXl,
            slidesToScroll: slidesToShow.slidesToShowXl,
          },
        },
        {
          breakpoint: breakPoint.lg,
          settings: {
            slidesToShow: slidesToShow.slidesToShowL,
            slidesToScroll: slidesToShow.slidesToShowL,
          },
        },
        {
          breakpoint: breakPoint.md,
          settings: {
            slidesToShow: slidesToShow.slidesToShowMd,
            slidesToScroll: slidesToShow.slidesToShowMd,
          },
        },
        {
          breakpoint: breakPoint.sm,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            dots: true,
            dotsClass: 'carousel-dots',
            arrows: false,
            customPaging: (i: number) => (
              <TrackedButton
                trackingName="recommendation_slider_dot"
                data={{
                  index: i,
                }}
              >
                {i}
              </TrackedButton>
            ),
          },
        },
      ],
    };
  };

  slickRef: Slider | null = null;

  render() {
    const { products, fullWidth, footerLabel, forceOpenInNewTab } = this.props;

    const sliderSettings = this.setSliderSettings(products.length);

    return (
      <div className="rc-DegreeProductsSlider rc-ProductsSlider">
        <Slider
          {...css(styles.sliderWidth, fullWidth ? styles.fullWidth : '')}
          {...sliderSettings}
          ref={(c) => {
            this.slickRef = c;
          }}
        >
          {products.map((item, i) => {
            return (
              // this outer div is needed for slider to behave nicely
              <div key={item?.id}>
                <div className="m-a-1s">
                  <BrowseDegreeCard
                    degree={item}
                    entityIndex={i}
                    showBorder={false}
                    footerLabel={footerLabel}
                    forceOpenInNewTab={forceOpenInNewTab}
                  />
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    );
  }
}
